/*
 * 落地页购买区公共交互 — 服务 snippets/nx-lp-shop.liquid（nx-lp-shop-gallery / nx-lp-shop-buy），
 * Aeroraise 与 Braidge 落地页共用；逻辑以 Aeroraise 已验收实现为底。
 * 每个 [data-lps-product] 根各自初始化、防重复：
 *   图集（按颜色换组 + 无缝循环 + 缩略图 / 下一张 / 点露出的下一张翻页 / 触控滑动 + 末尾视频弹层）、数量步进、
 *   变体联动（价格 / 折扣 / 按钮状态机）、Add to Cart（加购后唤起主题购物车抽屉；EU 站 Impulse 没有 cart-drawer section，
 *   派发 cart:build 让 theme.js 的 CartForm 自己拉购物车重建抽屉，再派发 cart:open 打开）、
 *   Buy Now（data-lps-clear-cart="true" 时先清空购物车，再加购去结账）、Size Chart 弹层。
 * 每次变体联动渲染后在根上派发（冒泡）nx-lps:variant，detail = { variant: 匹配到的变体或 null, complete: 是否选齐 }，
 * 页面级组件（如 Aeroraise 悬浮购买栏）据此同步价格 / 图片。
 * 配置全走 data-* 与内嵌 JSON，不含 Liquid 插值，可作为静态资源长期缓存。
 * 多语言（EU）：用户可见文案由 Liquid 从语言包写进根节点 data-lps-*-label（错误提示 / 视频 alt / 售罄等），
 * 这里的英文只是缺属性时的兜底。
 */
((doc) => {
  const SLIDE_MS = 450;
  const SLIDE_TRANSITION = 'transform .45s cubic-bezier(.25,.8,.25,1)';
  const SWIPE_MIN_PX = 35;
  const SWIPE_MAX_MS = 600;
  const FALLBACK_ERROR = 'Something went wrong. Please try again.';
  const FALLBACK_VIDEO = 'Product video';

  const errorText = (root) => root.dataset.lpsErrorLabel || FALLBACK_ERROR;

  const parseJSON = (node, fallback) => {
    try {
      return node ? JSON.parse(node.textContent) : fallback;
    } catch {
      return fallback;
    }
  };

  const toInt = (value, fallback = 0) => {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? fallback : parsed;
  };

  const escapeAttr = (value) => String(value ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;');

  const routeRoot = () => window.Shopify?.routes?.root ?? '/';

  const postJSON = async (path, body) => {
    const response = await fetch(`${routeRoot()}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || FALLBACK_ERROR);
    return data;
  };

  // 未选齐规格时（choose_alert 页面）：弹提示并聚焦第一个还没选的下拉；返回是否拦截
  const promptChoice = (root) => {
    const message = root.dataset.lpsChooseAlert;
    if (!message || root.dataset.lpsState !== 'choose') return false;
    window.alert(message);
    [...root.querySelectorAll('select[data-lps-option]')].find((select) => !select.value)?.focus();
    return true;
  };

  // 页面传了来源页标记（data-lps-landing，如 braidge_lp）时，加购成功后写进购物车属性 _landing（下划线开头，结账页不显示），
  // 随订单进 note attributes，由 Shopify Flow 给订单写来源（WEI-33）。紧跟 add.js 串行发，不与它并发写购物车；失败不影响购买
  const tagLanding = async (root) => {
    const landing = root.dataset.lpsLanding;
    if (!landing) return;
    try {
      await postJSON('cart/update.js', { attributes: { _landing: landing } });
    } catch (error) {
      console.warn('[nx-lps] Landing tag failed:', error);
    }
  };

  // 当前选中变体 + 数量 → 购物车行；未选齐 / 不可售时变体 id 为空，返回 null
  const lineOf = (root) => {
    const id = toInt(root.querySelector('[data-lps-variant-id]')?.value, 0);
    const quantity = Math.max(1, toInt(root.querySelector('[data-lps-qty-input]')?.value, 1));
    return id ? { id, quantity } : null;
  };

  /* ------------------------------------------------------------ 图集 */
  const createGallery = (root) => {
    const viewport = root.querySelector('[data-lps-viewport]');
    const track = root.querySelector('[data-lps-track]');
    const thumbStrip = root.querySelector('[data-lps-thumbs]');
    const nextButton = root.querySelector('[data-lps-next]');
    const data = parseJSON(root.querySelector('[data-lps-gallery-data]'), {});
    const videoUrl = data.video_url || '';
    const images = Array.isArray(data.images) ? data.images : [];

    if (!track) return { show: () => {} };

    const videoItem = (first) => ({
      type: 'video',
      url: videoUrl,
      poster: first?.url ?? '',
      thumb: first?.thumb ?? '',
      native: Boolean(first?.native),
      alt: root.dataset.lpsVideoLabel || FALLBACK_VIDEO
    });

    // 只显示当前颜色的图；该色一张图都没有时才回退到全部图片（同 Aeroraise 原逻辑，不拿别的颜色补位）；有视频时追加在末尾
    const imagesOf = (color) => {
      const own = color ? images.filter((image) => image.color === color) : images;
      const photos = (own.length ? own : images).map((image) => ({ ...image, type: 'image' }));
      return videoUrl ? [...photos, videoItem(photos[0])] : photos;
    };

    let items = [];
    let index = 0;
    let busy = false;

    // 落地页专用图集自带灰底，标 nx-lps__native 跳过 multiply 融底
    const nativeClass = (item) => (item.native ? ' class="nx-lps__native"' : '');

    const slideMarkup = (item, isClone) => (item.type === 'video'
      ? `
        <div class="nx-lps__slide nx-lps__slide--video"${isClone ? ' aria-hidden="true"' : ''} data-lps-video-trigger="${escapeAttr(item.url)}">
          <img${nativeClass(item)} src="${escapeAttr(item.poster || item.url)}" alt="${escapeAttr(item.alt)}" loading="lazy">
          <span class="nx-lps__play"><span>▶</span></span>
        </div>`
      : `
        <div class="nx-lps__slide"${isClone ? ' aria-hidden="true"' : ''}>
          <img${nativeClass(item)} src="${escapeAttr(item.url)}" alt="${escapeAttr(item.alt)}" loading="lazy">
        </div>`);

    const thumbMarkup = (item, position) => `
      <button
        class="nx-lps__thumb${item.type === 'video' ? ' nx-lps__thumb--video' : ''}"
        type="button"
        data-lps-thumb="${position}"
        aria-current="${position === 0}"
        aria-label="${escapeAttr(item.alt)}"
      >
        <img${nativeClass(item)} src="${escapeAttr(item.thumb || item.poster || item.url)}" alt="" loading="lazy">
        ${item.type === 'video' ? '<span class="nx-lps__play-badge">▶</span>' : ''}
      </button>`;

    const syncPager = () => {
      [...(thumbStrip?.children ?? [])].forEach((thumb, position) => {
        thumb.setAttribute('aria-current', String(position === index));
      });
    };

    const stepWidth = () => {
      const slide = track.querySelector('.nx-lps__slide');
      if (!slide) return 0;
      const gap = parseFloat(window.getComputedStyle(track).gap)
        || parseFloat(window.getComputedStyle(slide).marginRight) || 0;
      return slide.getBoundingClientRect().width + gap;
    };

    // 轨道位置：0 = 末张克隆，1…n = 真实图，n+1 / n+2 = 首张 / 第二张克隆（折返动画期间露出区始终有图）
    const moveTo = (position, animate) => {
      track.style.transition = animate ? SLIDE_TRANSITION : 'none';
      track.style.transform = `translateX(${-position * stepWidth()}px)`;
    };

    const go = (target) => {
      const count = items.length;
      if (busy || count < 2) return;

      const wrapped = (target + count) % count;
      const position = target < 0 ? 0 : target >= count ? count + 1 : wrapped + 1;
      index = wrapped;
      syncPager();
      moveTo(position, true);

      // 越过首尾时先滑到克隆片，动画结束后瞬移回对应真实图
      if (position !== wrapped + 1) {
        busy = true;
        window.setTimeout(() => {
          moveTo(wrapped + 1, false);
          busy = false;
        }, SLIDE_MS + 20);
      }
    };

    const next = () => go(index + 1);
    const prev = () => go(index - 1);

    const render = () => {
      const count = items.length;
      index = 0;

      if (count < 2) {
        track.innerHTML = count ? slideMarkup(items[0], false) : '';
        if (thumbStrip) thumbStrip.hidden = true;
        if (nextButton) nextButton.hidden = true;
        moveTo(0, false);
        return;
      }

      // 无缝循环（对齐 B 版 PDP snippets/nx-pdp-aeroraise.liquid）：末张克隆 + 真实图 + 首张克隆 + 第二张克隆
      const looped = [items[count - 1], ...items, items[0], items[1]];
      track.innerHTML = looped.map((item, position) => slideMarkup(item, position === 0 || position > count)).join('');
      if (thumbStrip) {
        thumbStrip.innerHTML = items.map(thumbMarkup).join('');
        thumbStrip.hidden = false;
      }
      if (nextButton) nextButton.hidden = false;
      syncPager();
      moveTo(1, false);
    };

    const show = (color) => {
      items = imagesOf(color);
      render();
    };

    thumbStrip?.addEventListener('click', ({ target }) => {
      const thumb = target.closest('[data-lps-thumb]');
      const position = thumb ? toInt(thumb.dataset.lpsThumb, -1) : -1;
      if (position >= 0 && position !== index) go(position);
    });

    nextButton?.addEventListener('click', next);

    // 视频弹层
    const videoModal = root.querySelector('[data-lps-video-modal]');
    const videoEl = videoModal?.querySelector('video');
    const openVideo = (src) => {
      if (!videoModal || !videoEl) return;
      videoEl.src = src || videoUrl;
      videoModal.hidden = false;
      videoEl.play().catch(() => {});
      doc.body.style.overflow = 'hidden';
    };
    const closeVideo = () => {
      if (!videoModal || !videoEl) return;
      videoEl.pause();
      videoEl.src = '';
      videoModal.hidden = true;
      doc.body.style.overflow = '';
    };
    videoModal?.querySelectorAll('[data-lps-video-close]').forEach((button) => {
      button.addEventListener('click', closeVideo);
    });
    doc.addEventListener('keydown', ({ key }) => {
      if (key === 'Escape' && videoModal && !videoModal.hidden) closeVideo();
    });

    // 点视频页打开弹层；点右侧露出的下一张（或左侧上一张）翻页
    track.addEventListener('click', ({ target }) => {
      const videoTrigger = target.closest('[data-lps-video-trigger]');
      if (videoTrigger) {
        openVideo(videoTrigger.dataset.lpsVideoTrigger);
        return;
      }
      const slide = target.closest('.nx-lps__slide');
      const position = slide ? [...track.children].indexOf(slide) : -1;
      if (position === index + 2) next();
      else if (position === index) prev();
    });

    // 视口尺寸变化后按新步长重新对位
    window.addEventListener('resize', () => {
      if (items.length > 1) moveTo(index + 1, false);
    });

    // 触控滑动：横向 > 35px 且 < 600ms 翻页，纵向优先放行给页面滚动（与 B 版 PDP 一致）
    let touch = null;
    viewport?.addEventListener('touchstart', ({ touches }) => {
      touch = touches.length === 1 && !busy
        ? { x: touches[0].clientX, y: touches[0].clientY, time: Date.now(), vertical: false }
        : null;
    }, { passive: true });

    viewport?.addEventListener('touchmove', ({ touches }) => {
      if (!touch || touch.vertical) return;
      const dx = touches[0].clientX - touch.x;
      const dy = touches[0].clientY - touch.y;
      if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) touch = { ...touch, vertical: true };
    }, { passive: true });

    viewport?.addEventListener('touchend', ({ changedTouches }) => {
      const start = touch;
      touch = null;
      if (!start || start.vertical) return;
      const dx = changedTouches[0].clientX - start.x;
      const quick = Date.now() - start.time < SWIPE_MAX_MS;
      if (Math.abs(dx) > SWIPE_MIN_PX && quick) {
        if (dx < 0) next();
        else prev();
      }
    }, { passive: true });

    return { show };
  };

  /* ---------------------------------------------------------- 数量步进 */
  const bindQuantity = (root) => {
    const input = root.querySelector('[data-lps-qty-input]');
    if (!input) return;

    const clamp = (value) => Math.max(1, toInt(value, 1));

    root.querySelectorAll('[data-lps-qty]').forEach((button) => {
      button.addEventListener('click', () => {
        input.value = clamp(clamp(input.value) + toInt(button.dataset.lpsQty));
      });
    });
    input.addEventListener('change', () => {
      input.value = clamp(input.value);
    });
  };

  /* ---------------------------------------------------------- 变体联动 */
  const bindVariants = (root, gallery) => {
    const { current, variants = [] } = parseJSON(root.querySelector('[data-lps-variants]'), {});
    if (!variants.length) {
      gallery.show();
      return () => {};
    }

    const colorIndex = toInt(root.dataset.lpsColorIndex, -1);
    const {
      lpsSaveLabel: saveLabel = '',
      lpsSoldOutLabel: soldOutLabel = 'Sold out',
      lpsUnavailableLabel: unavailableLabel = 'Unavailable',
      lpsChooseAlert: chooseAlert = ''
    } = root.dataset;

    const controls = [...root.querySelectorAll('[data-lps-option]')];
    const idInput = root.querySelector('[data-lps-variant-id]');
    const priceEl = root.querySelector('[data-lps-price]');
    const compareEl = root.querySelector('[data-lps-compare]');
    // inline 版式有两个折扣标（价格行胶囊 + 加购角标，按断点二选一显示），一起更新
    const saveEls = [...root.querySelectorAll('[data-lps-save]')];
    const colorValueEl = root.querySelector('[data-lps-color-value]');
    const actions = [
      { button: root.querySelector('[data-lps-add]'), text: root.querySelector('[data-lps-add-text]') },
      { button: root.querySelector('[data-lps-buynow]'), text: root.querySelector('[data-lps-buynow-text]') }
    ].filter(({ button }) => button);

    const isSelect = (control) => control.tagName === 'SELECT';
    const isChosen = (value) => value !== undefined && value !== '';
    const normalize = (value) => String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
    const priceKey = (variant) => [variant.price, variant.compare_at_price, variant.save].join('|');

    // 首屏：颜色 = Liquid 渲染的当前变体（色板 aria-pressed 同源），下拉一律留空待选（防浏览器回填表单）
    const selectIndexes = controls.filter(isSelect).map((control) => toInt(control.dataset.lpsOption, -1));
    controls.filter(isSelect).forEach((control) => {
      control.value = '';
    });
    const initial = variants.find(({ id }) => id === current) ?? variants[0];
    let selection = initial.options.map((value, optionIndex) => (selectIndexes.includes(optionIndex) ? '' : value));
    let shownColor = null;
    // 首屏价格已由 Liquid 按当前变体渲染好；价格没变就不重写，免得冲掉货币 App 的换算结果
    let shownPrice = priceKey(initial);

    const matches = ({ options }) => options.every((value, optionIndex) => (
      !isChosen(selection[optionIndex]) || normalize(selection[optionIndex]) === normalize(value)
    ));

    // 按钮状态机：choose（未选齐）→ unavailable（组合不存在）/ soldout（不可售）/ ready
    const stateOf = (match, complete) => {
      if (!match) return 'unavailable';
      if (!complete) return 'choose';
      return match.available ? 'ready' : 'soldout';
    };

    const labelFor = (state, button) => ({
      unavailable: unavailableLabel,
      soldout: soldOutLabel
    })[state] ?? button.dataset.lpsLabel ?? '';

    const renderPrice = (variant) => {
      const key = priceKey(variant);
      if (key === shownPrice) return;
      shownPrice = key;
      if (priceEl) priceEl.innerHTML = variant.price;
      if (compareEl) {
        compareEl.innerHTML = variant.compare_at_price ?? '';
        compareEl.hidden = !variant.compare_at_price;
      }
      saveEls.forEach((saveEl) => {
        saveEl.textContent = variant.save ? `${saveLabel} ${variant.save}%` : '';
        saveEl.hidden = !variant.save;
      });
    };

    const syncControls = () => {
      controls.forEach((control) => {
        const value = selection[toInt(control.dataset.lpsOption, -1)] ?? '';
        if (isSelect(control)) {
          if (control.value !== value) control.value = value;
          return;
        }
        const isActive = control.dataset.lpsValue === value;
        control.setAttribute('aria-pressed', String(isActive));
        control.classList.toggle('is-active', isActive);
      });
      const colorText = colorIndex >= 0 ? `- ${selection[colorIndex]}` : '';
      if (colorValueEl && colorText && colorValueEl.textContent !== colorText) colorValueEl.textContent = colorText;
    };

    const render = () => {
      const match = variants.find(matches);
      const complete = selection.every(isChosen);
      const state = stateOf(match, complete);

      syncControls();
      if (match) renderPrice(match);
      if (idInput) idInput.value = state === 'ready' ? match.id : '';

      // choose_alert 页面：未选齐（choose）也保持可点，点击时由加购 / Buy Now 处理弹提示
      root.dataset.lpsState = state;
      actions.forEach(({ button, text }) => {
        button.disabled = !(state === 'ready' || (chooseAlert && state === 'choose'));
        // 供 CSS 区分「规格未就绪 / 售罄」与「加购请求中」：前者折扣角标变灰，后者保持原色
        button.dataset.state = state;
        const label = labelFor(state, button);
        if (text && text.textContent !== label) text.textContent = label;
      });

      // 只有颜色真的变了才重建图集，换尺码不动
      const color = colorIndex >= 0 ? selection[colorIndex] : undefined;
      if (color !== shownColor) gallery.show(color);
      shownColor = color;

      root.dispatchEvent(new CustomEvent('nx-lps:variant', {
        bubbles: true,
        detail: { variant: match ?? null, complete }
      }));
    };

    const choose = (optionIndex, value) => {
      selection = selection.map((currentValue, position) => (position === optionIndex ? value : currentValue));
      render();
    };

    controls.forEach((control) => {
      const optionIndex = toInt(control.dataset.lpsOption, -1);
      if (isSelect(control)) {
        control.addEventListener('change', () => choose(optionIndex, control.value));
      } else {
        control.addEventListener('click', () => choose(optionIndex, control.dataset.lpsValue));
      }
    });

    render();
    return render;
  };

  /* ------------------------------------------------------ 加购与购物车抽屉 */
  const bindCart = (root, refresh) => {
    const addButton = root.querySelector('[data-lps-add]');
    const form = addButton?.form;
    if (!form) return;

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (promptChoice(root)) return;
      const item = lineOf(root);
      if (!item || addButton.disabled) return;

      addButton.disabled = true;
      try {
        await postJSON('cart/add.js', { items: [item] });
        // EU Impulse（assets/theme.js）：cart:build → CartForm.buildCart() 自己拉购物车重建抽屉内容与角标，
        // cart:open → CartDrawer.open()；与主题自带的 ajaxProduct:added（先 buildCart 再 open）同序
        doc.dispatchEvent(new CustomEvent('cart:build'));
        doc.dispatchEvent(new CustomEvent('cart:open'));
        await tagLanding(root);
      } catch (error) {
        console.error('[nx-lps] Add to cart error:', error);
        window.alert(error.message || errorText(root));
      } finally {
        refresh();
      }
    });
  };

  /* ---------------------------------------------------------- 立即购买 (Buy Now) */
  const bindBuyNow = (root, refresh) => {
    const buyButton = root.querySelector('[data-lps-buynow]');
    if (!buyButton) return;

    const addButton = root.querySelector('[data-lps-add]');
    const clearCart = root.dataset.lpsClearCart === 'true';

    const settle = () => {
      buyButton.classList.remove('is-loading');
      refresh();
    };

    buyButton.addEventListener('click', async () => {
      if (promptChoice(root)) return;
      const item = lineOf(root);
      if (!item || buyButton.disabled) return;

      buyButton.disabled = true;
      buyButton.classList.add('is-loading');
      if (addButton) addButton.disabled = true;

      try {
        if (clearCart) {
          const cleared = await fetch(`${routeRoot()}cart/clear.js`, { method: 'POST' });
          if (!cleared.ok) throw new Error(errorText(root));
        }
        await postJSON('cart/add.js', { items: [item] });
        await tagLanding(root);
        window.location.href = `${routeRoot()}checkout`;
      } catch (error) {
        console.error('[nx-lps] Buy Now error:', error);
        window.alert(error.message || errorText(root));
        settle();
      }
    });

    // 从结账页返回（bfcache 恢复）时解除转圈
    window.addEventListener('pageshow', ({ persisted }) => {
      if (persisted && buyButton.classList.contains('is-loading')) settle();
    });
  };

  /* ---------------------------------------------------------- 尺码表弹层 (Size Chart) */
  const bindSizeChart = (root) => {
    const triggers = [...root.querySelectorAll('[data-lps-sizechart]')];
    const modal = root.querySelector('[data-lps-sizechart-modal]');
    if (!triggers.length || !modal) return;

    const open = () => {
      modal.hidden = false;
      doc.body.style.overflow = 'hidden';
      window.nxZoomReset?.(modal.querySelector('[data-nx-zoom]'));
    };
    const close = () => {
      modal.hidden = true;
      doc.body.style.overflow = '';
    };

    triggers.forEach((trigger) => trigger.addEventListener('click', open));
    modal.querySelectorAll('[data-lps-sizechart-close]').forEach((button) => {
      button.addEventListener('click', close);
    });
    doc.addEventListener('keydown', ({ key }) => {
      if (key === 'Escape' && !modal.hidden) close();
    });
  };

  /* -------------------------------------------------------------- 启动 */
  const init = (root) => {
    if (root.dataset.lpsReady) return;
    root.dataset.lpsReady = 'true';

    const gallery = createGallery(root);
    bindQuantity(root);
    const refresh = bindVariants(root, gallery);
    bindCart(root, refresh);
    bindBuyNow(root, refresh);
    bindSizeChart(root);
  };

  const boot = () => doc.querySelectorAll('[data-lps-product]').forEach(init);

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})(document);
